/**
 * In-class chat wire format, over the `ischool-chat` data-channel topic.
 *
 * Trust model is the classroom's: the SENDER identity comes from the LiveKit
 * envelope (server-minted JWT), never from these payloads; teacher-only kinds
 * are checked on receipt with isTeacher(from); and every moderation kind
 * carries the server-minted `rev`, so a stale event can never flip state
 * backwards. The server's copy (chat_history / chat_moderation endpoints) is
 * the durable truth; this channel is the fast path.
 */

export const CHAT_TOPIC = 'ischool-chat'

/** Where chat image objects are publicly readable. Matches the API bucket. */
export const CHAT_UPLOADS_BASE =
  'https://ischool-meet-chat-uploads.s3.eu-central-1.amazonaws.com'
export const attachmentUrl = (key: string) => `${CHAT_UPLOADS_BASE}/${key}`

/** Ring buffer size — what a client keeps and what "history" means in-room. */
export const RING = 200
/** One reliable packet's payload budget, matching the surface chunker. */
export const SNAPSHOT_CHUNK_BYTES = 9_000
/** How much of the ring a snapshot replays to a late joiner. */
export const SNAPSHOT_MESSAGES = 50
/**
 * Max message body in BYTES, not characters — Arabic is the default language
 * and its characters are two bytes each; a char cap would double the wire cost.
 * Matches Livekit::ChatMessage::MAX_BODY_BYTES server-side.
 */
export const MAX_BODY_BYTES = 2048
/** Sender clocks skew by minutes in the field; inbound `at` is clamped. */
export const CLOCK_SLACK_MS = 5 * 60_000
/** Send rate: a small burst, then one message per interval. */
export const SEND_BURST = 3
export const SEND_INTERVAL_MS = 500
/**
 * How many images one drop/paste may leave waiting.
 *
 * Deliberately small. A queued entry pins the original `File` — an OS file
 * handle and, for a camera photo, several megabytes the browser may materialise
 * at any moment — and the decode that drains the queue is the single most
 * expensive thing this app asks of a 2 GB Android. Four covers "here are the
 * three worksheet photos"; fifty is a phone running out of memory mid-lesson.
 */
export const CHAT_QUEUE_CAP = 4

/**
 * An IMAGE on a chat message. Keys are server-issued; clients build the URL.
 * `kind` is absent on every image ever sent — images predate the union — so
 * the discriminator only ever has to name the NEW arm (file, LK-009).
 */
export interface ChatImageAttachment {
  kind?: 'image'
  key: string
  thumb_key?: string | null
  w: number
  h: number
  bytes?: number
  name?: string | null
}

/**
 * A non-image FILE on a chat message (LK-009: PDFs). The object is PRIVATE —
 * unlike images there is no public bucket URL; it is opened through a
 * short-lived presigned GET the API mints for run participants, so the wire
 * carries only the metadata the chip renders: filename, size, content type.
 */
export interface ChatFileAttachment {
  kind: 'file'
  key: string
  filename: string
  size: number
  content_type: string
}

export type ChatAttachment = ChatImageAttachment | ChatFileAttachment

/** Files are the only arm that names itself — every legacy image lacks `kind`. */
export const isFileAttachment = (a: ChatAttachment): a is ChatFileAttachment => a.kind === 'file'

export interface ChatMessage {
  id: string
  identity: string
  name: string
  /** Epoch ms, sender-claimed but receive-clamped. UI orders by (at, id). */
  at: number
  /** null once tombstoned. */
  body: string | null
  attachment?: ChatAttachment | null
  deleted: boolean
  /** Local-only: true while this client's own message awaits its first echo. */
  mine?: boolean
  /** Local-only: replayed history — never counted toward the unread badge. */
  silent?: boolean
  /** Local-only: an object URL for a still-uploading image, revoked on confirm. */
  localPreview?: string
  /** Local-only: 'uploading' | 'failed' while an image is in flight. */
  uploadState?: 'uploading' | 'failed'
}

export interface ChatModerationState {
  rev: number
  freeze: boolean
  mutes: string[]
}

/**
 * Moderation packets carry the FULL post-action state, not just their own
 * delta.
 *
 * With deltas alone, two actions racing their HTTP responses lose a field
 * permanently: freeze commits rev 1, mute commits rev 2, the mute response
 * returns first and publishes {rev:2, mute}, every client advances to rev 2 —
 * and then REJECTS the rev-1 freeze packet as stale. The class believes it is
 * unfrozen forever. Shipping the server's whole {freeze, mutes} with each
 * event makes any single packet sufficient to reach the correct state.
 * `state` is optional only so a client mid-rollout can still read old packets.
 */
export type ChatWire =
  | { kind: 'chat'; id: string; body: string; at: number; attachment?: ChatAttachment }
  | { kind: 'chat_delete'; id: string; rev: number; state?: ChatModerationState }
  | { kind: 'chat_freeze'; on: boolean; rev: number; state?: ChatModerationState }
  | { kind: 'chat_mute'; identity: string; on: boolean; rev: number; state?: ChatModerationState }
  | { kind: 'chat_request' }
  | { kind: 'chat_snapshot_chunk'; sid: string; i: number; n: number; part: string }

export interface ChatSnapshot {
  state: ChatModerationState
  tombstones: string[]
  messages: Array<{
    id: string
    identity: string
    name: string | null
    at: number
    body: string | null
    // Untrusted at the boundary — a peer or the server can send anything here.
    // applySnapshot narrows it through sanitizeIncomingAttachment before trust.
    attachment?: unknown
    deleted: boolean
  }>
}
